'use client';

/**
 * Browser breadcrumbs to Logfire (LUM-69).
 *
 * Call sites use `breadcrumb('call_start', { call_id })` — this
 * replaced the Mixpanel client entirely (LUM-70); browser telemetry is
 * first-party only.
 *
 * Events are batched and POSTed to /api/telemetry/breadcrumb where the
 * worker forwards them to Logfire under `service.name=discovery-dialogues`
 * tagged with `breadcrumb_source: "browser"`.
 *
 * LOAD-BEARING PRIVACY RULE: pure metadata only — never call this with
 * transcripts, message text, summaries, recording URLs, or device labels.
 * The server enforces an allowlist+denylist regardless of what we send,
 * but we sanitize here too as defense in depth. See
 * `feedback_no_client_data_in_logfire` memory.
 *
 * Telemetry failure must never affect calls — every path is wrapped in
 * try/catch and the queue is bounded.
 */

import { getSupabaseBrowserClient } from '../supabase/browser';

type EventName =
  // Realtime call lifecycle
  | 'call_start'
  | 'call_end'
  | 'call_error'
  // register-call binding chain (LUM-81)
  | 'register_call_id_attempt'
  | 'register_call_id_success'
  | 'register_call_id_failed'
  // Interview flow
  | 'interview_instructions_view'
  | 'interview_details_confirmed'
  | 'interview_start_click'
  | 'interview_user_end_click'
  | 'interview_error'
  | 'interview_retry_attempt'
  | 'interview_session_created'
  | 'interview_time_warning_5min'
  | 'interview_time_warning_1min'
  | 'interview_time_capped'
  | 'interview_limit_resolved'
  // In-call mic health (LUM-82): bucketed levels only, every 15s
  | 'interview_audio_level_sample'
  // Audio device + permissions
  | 'mic_permission'
  | 'mic_permission_check'
  | 'device_enum_start'
  | 'device_enum_ready'
  // Mic test page
  | 'mic_test_view'
  | 'mic_test_start'
  | 'mic_test_level_sample'
  | 'mic_test_result'
  | 'mic_test_skipped'
  // Page/network state
  | 'page_visibility_change'
  | 'online_offline_change'
  | 'beforeunload_during_call'
  // Auth flow
  | 'login_view'
  | 'magic_link_request'
  | 'magic_link_login_success'
  | 'magic_link_login_failed'
  | 'page_view'
  | 'theme_progress';

interface QueuedEvent {
  name: EventName;
  client_ts: number;
  attrs: Record<string, unknown>;
}

const FORBIDDEN_ATTRS = new Set<string>([
  'transcript',
  'transcript_text',
  'message',
  'message_body',
  'text',
  'summary',
  'recording_url',
  'recording',
  'audio_url',
  'audio_data',
  'mic_label',
  'device_label',
  'raw_error_body',
  'error_body',
  'authorization',
  'token',
  'access_token',
  'refresh_token',
]);

const MAX_QUEUE = 100;
const BATCH_SIZE = 10;
const FLUSH_MS = 3000;

let sessionId: string | null = null;
let initialized = false;
let cachedContext: Record<string, unknown> | null = null;
let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushInFlight = false;
let cachedToken: string | null = null;
let tokenFetchAt = 0;

/**
 * Generate a UUIDv4-shaped session id. Uses crypto.randomUUID where
 * available (Workers + every modern browser) and falls back to
 * crypto.getRandomValues + manual v4 assembly. Returns null if neither
 * is available — the server schema makes session_id optional, so we
 * just skip the correlation tag rather than ship insecure randomness.
 */
function newSessionId(): string | null {
  try {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
      bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
      const hex: string[] = [];
      for (let i = 0; i < 16; i += 1) {
        hex.push(bytes[i].toString(16).padStart(2, '0'));
      }
      return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
    }
  } catch (_err) {
    // Both crypto paths threw (very old engine) — fall through to null.
  }
  return null;
}

interface NavigatorConnection {
  effectiveType?: string;
  type?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

function getConnection(): NavigatorConnection | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const n = navigator as Navigator & {
    connection?: NavigatorConnection;
    mozConnection?: NavigatorConnection;
    webkitConnection?: NavigatorConnection;
  };
  return n.connection ?? n.mozConnection ?? n.webkitConnection;
}

interface ParsedUA {
  browser_name: string;
  browser_version: string;
  os_name: string;
  os_version: string;
  device_type: 'mobile' | 'tablet' | 'desktop';
}

/**
 * Lightweight UA parsing — good enough for filtering in Logfire. We
 * deliberately don't pull in ua-parser-js to keep the bundle small.
 *
 * All patterns are anchored, use non-greedy quantifiers where they
 * follow capture groups, and run against a string capped at 500 chars
 * upstream — no ReDoS surface.
 */
function parseUserAgent(ua: string): ParsedUA {
  const result: ParsedUA = {
    browser_name: 'unknown',
    browser_version: 'unknown',
    os_name: 'unknown',
    os_version: 'unknown',
    device_type: 'desktop',
  };

  // Order matters: Edge before Chrome, Chrome before Safari, etc. Use
  // a per-pattern match() over global RegExp.$1 so the regex state is
  // local rather than relying on global side effects.
  const browserPatterns: Array<[RegExp, string]> = [
    [/Edg\/(\S+)/i, 'edge'],
    [/Firefox\/(\S+)/i, 'firefox'],
    [/Chrome\/(\S+)/i, 'chrome'],
    [/Version\/(\S+?)\s.*?Safari/i, 'safari'],
    [/Safari\/(\S+)/i, 'safari'],
  ];
  for (const [pattern, name] of browserPatterns) {
    const match = ua.match(pattern);
    if (match) {
      result.browser_name = name;
      result.browser_version = match[1];
      break;
    }
  }

  const osPatterns: Array<[RegExp, string, (v: string) => string]> = [
    [/Windows NT ([\d.]+)/i, 'windows', v => v],
    [/Mac OS X ([\d_.]+)/i, 'macos', v => v.replaceAll('_', '.')],
    [/Android (\S+);/i, 'android', v => v],
    [/iPhone OS ([\d_]+)/i, 'ios', v => v.replaceAll('_', '.')],
  ];
  for (const [pattern, name, normalise] of osPatterns) {
    const match = ua.match(pattern);
    if (match) {
      result.os_name = name;
      result.os_version = normalise(match[1]);
      break;
    }
  }
  if (result.os_name === 'unknown' && /Linux/i.test(ua)) {
    result.os_name = 'linux';
  }

  if (/iPad|Tablet/i.test(ua)) {
    result.device_type = 'tablet';
  } else if (/Mobi|Android|iPhone/i.test(ua)) {
    result.device_type = 'mobile';
  }

  return result;
}

function buildSessionContext(): Record<string, unknown> {
  if (cachedContext) return cachedContext;
  if (typeof window === 'undefined') return {};

  const ua = navigator.userAgent || '';
  const parsed = parseUserAgent(ua);
  const conn = getConnection();

  let referrerDomain: string | null = null;
  try {
    if (document.referrer) {
      referrerDomain = new URL(document.referrer).hostname || null;
    }
  } catch (_err) {
    // Malformed referrer URL — drop it, the rest of the context is fine.
  }

  const micPermission: string | null = null;
  // Permissions API check is async — fired in `initBreadcrumbs` and
  // merged into context once resolved.

  cachedContext = {
    user_agent_raw: ua.slice(0, 500),
    browser_name: parsed.browser_name,
    browser_version: parsed.browser_version,
    os_name: parsed.os_name,
    os_version: parsed.os_version,
    device_type: parsed.device_type,
    viewport_w: window.innerWidth,
    viewport_h: window.innerHeight,
    screen_w: window.screen?.width,
    screen_h: window.screen?.height,
    device_pixel_ratio: window.devicePixelRatio,
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer_domain: referrerDomain,
    connection_effective_type: conn?.effectiveType ?? null,
    connection_downlink: conn?.downlink ?? null,
    connection_rtt: conn?.rtt ?? null,
    connection_save_data: conn?.saveData ?? null,
    online_at_start: navigator.onLine,
    mic_permission: micPermission,
    page_path: window.location.pathname,
  };
  return cachedContext;
}

async function probeMicPermission(): Promise<void> {
  if (typeof navigator === 'undefined') return;
  const perms = (navigator as Navigator & { permissions?: Permissions })
    .permissions;
  if (!perms?.query) return;
  try {
    const status = await perms.query({ name: 'microphone' as PermissionName });
    if (cachedContext) {
      cachedContext.mic_permission = status.state;
    }
    status.onchange = () => {
      if (cachedContext) {
        cachedContext.mic_permission = status.state;
      }
      void track('mic_permission', { state: status.state });
    };
  } catch (_err) {
    // Permissions.query with 'microphone' isn't supported everywhere
    // (older Safari) — fail silent so the rest of the breadcrumb path works.
  }
}

function sanitizeAttrs(
  attrs: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!attrs) return {};
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(attrs)) {
    const key = k.toLowerCase();
    if (FORBIDDEN_ATTRS.has(key)) continue;
    if (/transcript|message|summary|recording|audio_data/i.test(key)) continue;
    if (v === undefined || v === null) {
      out[k] = null;
      continue;
    }
    if (typeof v === 'object') {
      // Don't ship arbitrary objects — they may contain unsanitized fields.
      out[k] = '[object]';
      continue;
    }
    if (typeof v === 'string' && v.length > 500) {
      out[k] = `${v.slice(0, 500)}…`;
      continue;
    }
    out[k] = v;
  }
  return out;
}

async function getToken(): Promise<string | null> {
  const now = Date.now();
  // Re-fetch every 30s; otherwise getSession's microtask cost is fine
  // but caching reduces churn during high-frequency events.
  if (cachedToken && now - tokenFetchAt < 30_000) return cachedToken;
  try {
    const supabase = getSupabaseBrowserClient();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    cachedToken = session?.access_token ?? null;
    tokenFetchAt = now;
    return cachedToken;
  } catch (_err) {
    // Supabase client unreachable — drop the batch rather than spin.
    return null;
  }
}

async function flush(opts: { keepalive?: boolean } = {}): Promise<void> {
  if (flushInFlight && !opts.keepalive) return;
  if (queue.length === 0) return;
  flushInFlight = true;

  const batch = queue.splice(0, BATCH_SIZE);

  try {
    const token = await getToken();
    if (!token) {
      // Drop the batch silently — we can't authenticate. Don't re-queue
      // forever or the queue grows unbounded.
      return;
    }

    // Omit session_id if null — server schema expects UUID or absent,
    // not null. (Null happens when both crypto.randomUUID and
    // crypto.getRandomValues are unavailable; extremely rare.)
    const payload: { session_id?: string; events: typeof batch } = {
      events: batch,
    };
    if (sessionId) payload.session_id = sessionId;
    const body = JSON.stringify(payload);

    await fetch('/api/telemetry/breadcrumb', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body,
      keepalive: opts.keepalive === true,
    });
  } catch (_err) {
    // Telemetry failure must never affect the call — swallow silently.
    // (This is the load-bearing contract of the module: calls take
    // priority over breadcrumbs.)
  } finally {
    flushInFlight = false;
    if (queue.length > 0) {
      scheduleFlush();
    }
  }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_MS);
}

export function initBreadcrumbs(): void {
  if (typeof window === 'undefined') return;
  if (initialized) return;
  initialized = true;

  sessionId = newSessionId();
  buildSessionContext();
  void probeMicPermission();

  // Lifecycle listeners — auto-emit visibility, online/offline,
  // and best-effort unload flush via keepalive.
  try {
    document.addEventListener('visibilitychange', () => {
      void track('page_visibility_change', {
        state: document.visibilityState,
      });
      if (document.visibilityState === 'hidden') {
        void flush({ keepalive: true });
      }
    });

    window.addEventListener('online', () =>
      track('online_offline_change', { online: true }),
    );
    window.addEventListener('offline', () =>
      track('online_offline_change', { online: false }),
    );

    window.addEventListener('pagehide', () => {
      void flush({ keepalive: true });
    });
    window.addEventListener('beforeunload', () => {
      void flush({ keepalive: true });
    });
  } catch (_err) {
    // Listener registration failed (sandboxed iframe / locked-down env).
    // Continue without lifecycle hooks rather than block init.
  }
}

export function setBreadcrumbSession(
  opts: { sessionId?: string; userId?: string } = {},
): void {
  if (opts.sessionId) sessionId = opts.sessionId;
  if (opts.userId && cachedContext) {
    cachedContext.user_role = 'participant';
  }
}

export async function track(
  name: EventName,
  attrs?: Record<string, unknown>,
): Promise<void> {
  try {
    if (typeof window === 'undefined') return;
    if (!initialized) initBreadcrumbs();

    const ctx = buildSessionContext();
    const merged = {
      ...ctx,
      ...sanitizeAttrs(attrs),
    };

    if (queue.length >= MAX_QUEUE) {
      // Bounded queue — drop oldest to keep memory steady.
      queue.shift();
    }
    queue.push({
      name,
      client_ts: Date.now(),
      attrs: merged,
    });

    if (queue.length >= BATCH_SIZE) {
      void flush();
    } else {
      scheduleFlush();
    }
  } catch (_err) {
    // Never throw from telemetry. Anything that broke here would only
    // affect a single breadcrumb, not the call.
  }
}

/**
 * Test hook — for unit tests only. Clears module state.
 */
export function __resetForTests(): void {
  sessionId = null;
  initialized = false;
  cachedContext = null;
  queue = [];
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  flushInFlight = false;
  cachedToken = null;
  tokenFetchAt = 0;
}

export type { EventName };
